import * as React from 'react';

import SpeedTest from '@cloudflare/speedtest';

import { NetworkMonitor } from 'root/apps/NetworkHealthTool/components/NetworkMonitor';
import {
  ClassificationName,
  ClassificationNames,
  NetworkSpeedMetrics,
  type ScoreResults,
} from 'root/apps/NetworkHealthTool/services/NetworkHealthToolService';
import { useSyncNetworkTestReports } from 'root/apps/NetworkHealthTool/services/useNetworkHealthToolReportingService';
import { useNetworkTestService } from 'root/apps/NetworkHealthTool/services/useNetworkHealthToolService';
import {
  ChannelMessage,
  createBroadcastChannel,
  NetworkEventType,
} from 'root/apps/NetworkHealthTool/utils/channelUtils';
import {
  computeNextRunFromElapsed,
  increaseBackoff,
  resetBackoff,
} from 'root/apps/NetworkHealthTool/utils/timingUtils';
import Modules from 'root/apps/shared_modules/Modules';
import { logError } from 'root/datadog/datadogLogs';

export type SpeedTestMeasurement =
  | { type: 'latency'; numPackets: number }
  | { type: 'download'; bytes: number; count: number; bypassMinDuration?: boolean }
  | { type: 'upload'; bytes: number; count: number; bypassMinDuration?: boolean }
  | { type: 'packetLoss'; numPackets?: number; responsesWaitTime?: number };

const TEST_CONFIG: SpeedTestMeasurement[] = [
  { type: 'latency', numPackets: 1 },
  { type: 'latency', numPackets: 20 },
  { type: 'download', bytes: 100_000, count: 1, bypassMinDuration: true }, // 100KB
  { type: 'download', bytes: 4_000_000, count: 2 }, // 4MB x 2
  { type: 'upload', bytes: 4_000_000, count: 2 }, // 4MB x 2
];

/**
 * Schedules and runs periodic network speed tests using a web worker.
 * Skips tests if the tab is inactive.
 * Logs results or errors to the console.
 */
export const NetworkHealthTool: React.FC = () => {
  const [speedTestInstance] = React.useState(
    new SpeedTest({ measurements: TEST_CONFIG, autoStart: false })
  );
  const [channel] = React.useState(createBroadcastChannel());
  const runTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const service = useNetworkTestService();
  useSyncNetworkTestReports();

  React.useEffect(() => {
    try {
      service.deleteOldNetworkTests();
    } catch (error) {
      logError(error, {
        message: 'Unable to delete old network tests',
        file: 'NetworkHealthTool.ts',
        source: 'NetworkHealthTool',
      });
    }
  }, [service]);

  React.useEffect(() => {
    if (!Modules.hasModule('support.network_health_tool.debug')) return;

    const mockMetrics: NetworkSpeedMetrics = {
      download: 0,
      upload: 0,
      latency: 0,
      jitter: 0,
      downLoadedLatency: 0,
      downLoadedJitter: 0,
      upLoadedLatency: 0,
      upLoadedJitter: 0,
      packetLoss: 0,
    };
    window.debugNetworkHealthToolService = service;
    window.debugNetworkHealthToolAddEntry = async (classificationName: string) => {
      if (!Object.values(ClassificationNames).includes(classificationName as ClassificationName)) {
        // not sending to datadog, just console error for devs
        console.error(
          `Invalid classification name: ${classificationName}. Must be one of: ${Object.values(
            ClassificationNames
          ).join(', ')}`
        );
        return;
      }
      await service.addNetworkTest(mockMetrics, {
        rtc: {
          classificationIdx: 0,
          classificationName: classificationName as ClassificationName,
          points: 0,
        },
      });
    };

    const addNetworkTestBkp = service.addNetworkTest;
    window.debugNetworkHealthToolBrokenStateStart = () => {
      service.addNetworkTest = () => {
        throw new Error('Simulated failure');
      };
    };
    window.debugNetworkHealthToolBrokenStateStop = () => {
      service.addNetworkTest = addNetworkTestBkp;
    };
  }, [service]);

  const getNextRunTimeoutTime = React.useCallback(async (): Promise<number> => {
    let elapsed: number | null = null;
    try {
      elapsed = await service.getTimeSinceLastTest();
    } catch (error) {
      increaseBackoff();
      logError(error, {
        message: 'NetworkHealthTool getNextRunTimeoutTime failed',
        file: 'NetworkHealthTool.ts',
        source: 'NetworkHealthTool',
      });
    }
    return computeNextRunFromElapsed(elapsed);
  }, [service]);

  /**
   * Executes a network speed test if one is not already running and the tab is visible.
   * Uses the configured test parameters and logs the result or error to the console.
   * Ensures only one test runs at a time.
   */
  const runTest = React.useCallback(async () => {
    if (speedTestInstance.isRunning) return;

    if (document.visibilityState !== 'visible') return;
    if (document.readyState !== 'complete') return;

    const timeoutTime = await getNextRunTimeoutTime();
    if (timeoutTime > 0) {
      if (runTimeoutRef.current) clearTimeout(runTimeoutRef.current);
      runTimeoutRef.current = setTimeout(runTest, timeoutTime);
      return;
    }

    speedTestInstance.restart();

    Modules.hasModule('support.network_health_tool.debug') &&
      console.log(`NetworkHealthTool: start test - ${new Date().toISOString()}`);
  }, [getNextRunTimeoutTime, speedTestInstance]);

  React.useEffect(() => {
    speedTestInstance.onFinish = async (results) => {
      let summary: ReturnType<typeof results.getSummary> | null = null;
      let scores: ReturnType<typeof results.getScores> | null = null;

      try {
        summary = results.getSummary() as NetworkSpeedMetrics;
        scores = results.getScores() as ScoreResults;

        if (!scores.rtc) {
          throw new Error('Speedtest result is missing RTC score');
        }

        await service.addNetworkTest(summary, scores);

        resetBackoff();
      } catch (error) {
        logError(error, {
          message: `SpeedTest onFinish handling failed.`,
          file: 'NetworkHealthTool.tsx',
          source: 'NetworkHealthTool',
          summary,
          scores,
        });

        increaseBackoff();
        const message: ChannelMessage = {
          type: NetworkEventType.NETWORK_TEST_RUN_FAILED,
          payload: null,
        };
        channel.postMessage(message);
      }

      getNextRunTimeoutTime().then((timeoutTime) => {
        if (runTimeoutRef.current) clearTimeout(runTimeoutRef.current);
        runTimeoutRef.current = setTimeout(runTest, timeoutTime);
      });

      Modules.hasModule('support.network_health_tool.debug') &&
        console.log(`NetworkHealthTool: finish test - ${new Date().toISOString()}`);
    };

    /**
     *  onError is called when a specific measurement of a speedtest config fails.
     *  A speedtest will always trigger onFinish, even it all measurements failed.
     *  The onFinish result payload will contain `undefined` for each failed measurement.
     *  Thus, we treat onError as a non-critical event and just log it.
     */
    speedTestInstance.onError = (error) => {
      logError(`SpeedTest onError triggered with: ${error ?? 'Unknown error'}`, {
        file: 'NetworkHealthTool.tsx',
        source: 'NetworkHealthTool',
      });
    };
  }, [channel, getNextRunTimeoutTime, runTest, service, speedTestInstance]);

  React.useEffect(() => {
    let hasStarted: boolean = false;

    const startNetworkTest = () => {
      if (hasStarted) return;
      hasStarted = true;
      getNextRunTimeoutTime().then((timeoutTime) => {
        if (runTimeoutRef.current) clearTimeout(runTimeoutRef.current);
        runTimeoutRef.current = setTimeout(runTest, timeoutTime);
      });

      Modules.hasModule('support.network_health_tool.debug') &&
        console.log(`NetworkHealthTool: init interval - ${new Date().toISOString()}`);
    };

    const endNetworkTest = () => {
      speedTestInstance.pause();
      hasStarted = false;
      if (runTimeoutRef.current) {
        clearTimeout(runTimeoutRef.current);
        runTimeoutRef.current = null;
      }

      Modules.hasModule('support.network_health_tool.debug') &&
        console.log(`NetworkHealthTool: end interval - pause - : ${new Date().toISOString()}`);
    };

    const handleVisibilityChange = () => {
      if (document.readyState !== 'complete') return;
      if (document.visibilityState === 'visible') {
        startNetworkTest();
      } else {
        endNetworkTest();
      }
    };

    const handleLoad = () => {
      if (document.readyState === 'complete' && document.visibilityState === 'visible') {
        startNetworkTest();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('load', handleLoad);

    handleLoad(); // in case the load event already fired

    return () => {
      endNetworkTest();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('load', handleLoad);
    };
  }, [getNextRunTimeoutTime, runTest, service, speedTestInstance]);

  return <NetworkMonitor />;
};
